import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, CheckCircle, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_SJttalW9ccoKSt";

let razorpayScriptPromise = null;

function loadRazorpayScript() {
  if (window.Razorpay) {
    return Promise.resolve(true);
  }

  if (!razorpayScriptPromise) {
    razorpayScriptPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  return razorpayScriptPromise;
}

async function sendConfirmationEmail({ email, studentName, registrationCode, mobile, amount, cart }) {
  return axios.post(`${API_BASE_URL}/email/send-confirmation`, {
    email,
    student_name: studentName,
    registration_code: registrationCode,
    mobile,
    amount: String(amount),
    courses: cart
      .map((item) => `${item.examName}${item.optionName ? `-${item.optionName}` : ""}`)
      .join(", "),
  });
}

function RazorpayPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");
  const openedRef = useRef(false);

  const paymentData = location.state;

  useEffect(() => {
    if (!paymentData?.registrationCode || !paymentData?.amount) {
      navigate("/register", { replace: true });
      return;
    }

    if (openedRef.current) {
      return;
    }
    openedRef.current = true;

    const openCheckout = async () => {
      try {
        if (!RAZORPAY_KEY) {
          throw new Error("Razorpay key is missing in the frontend environment.");
        }

        const loaded = await loadRazorpayScript();
        if (!loaded) {
          throw new Error("Failed to load Razorpay checkout.");
        }

        const orderResponse = await axios.post(`${API_BASE_URL}/payments/razorpay/order`, {
          registrationCode: paymentData.registrationCode,
          amount: paymentData.amount,
          customerName: paymentData.formData?.studentName,
          customerEmail: paymentData.formData?.email,
          customerPhone: paymentData.formData?.mobile,
        });

        const order = orderResponse.data?.data;
        if (!order?.orderId) {
          throw new Error("Payment order could not be created.");
        }

        const razorpay = new window.Razorpay({
          key: RAZORPAY_KEY,
          amount: order.amount,
          currency: order.currency || "INR",
          order_id: order.orderId,
          name: "Unified Council",
          description: `Registration ${paymentData.registrationCode}`,
          prefill: {
            name: paymentData.formData?.studentName,
            email: paymentData.formData?.email,
            contact: paymentData.formData?.mobile,
          },
          theme: {
            color: "#0ea5e9",
          },
          modal: {
            ondismiss: async () => {
              try {
                await axios.post(`${API_BASE_URL}/payments/razorpay/failure`, {
                  registrationCode: paymentData.registrationCode,
                  reason: "Payment cancelled by user",
                });
              } catch (_error) {
                // Ignore failure recording errors in the UI flow.
              }

              setStatus("failed");
              setError("Payment was cancelled before completion.");
            },
          },
          handler: async (response) => {
            try {
              const verifyResponse = await axios.post(`${API_BASE_URL}/payments/razorpay/verify`, {
                registrationCode: paymentData.registrationCode,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });

              if (!verifyResponse.data?.success) {
                throw new Error("Payment verification failed.");
              }

              try {
                await sendConfirmationEmail({
                  email: paymentData.formData?.email,
                  studentName: paymentData.formData?.studentName,
                  registrationCode: paymentData.registrationCode,
                  mobile: paymentData.formData?.mobile,
                  amount: paymentData.amount,
                  cart: paymentData.cart || [],
                });
              } catch (_emailError) {
                toast.error("Payment succeeded, but the confirmation email could not be sent.");
              }

              setStatus("success");
              toast.success(`Payment successful. Ref: ${paymentData.registrationCode}`);
            } catch (verifyError) {
              setStatus("failed");
              setError(verifyError.response?.data?.message || verifyError.message || "Payment verification failed.");
            }
          },
        });

        razorpay.on("payment.failed", async (response) => {
          try {
            await axios.post(`${API_BASE_URL}/payments/razorpay/failure`, {
              registrationCode: paymentData.registrationCode,
              reason: response.error?.description || "Payment failed",
            });
          } catch (_error) {
            // Ignore failure recording errors in the UI flow.
          }

          setStatus("failed");
          setError(response.error?.description || "Payment failed.");
        });

        razorpay.open();
      } catch (checkoutError) {
        setStatus("failed");
        setError(checkoutError.response?.data?.message || checkoutError.message || "Unable to start payment.");
      }
    };

    openCheckout();
  }, [navigate, paymentData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
        {status === "processing" && (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-sky-600 animate-spin" />
            </div>
            <h1 className="text-3xl font-black text-slate-800 mb-2">Opening Razorpay</h1>
            <p className="text-slate-600 mb-6">Complete the payment popup to finish your registration.</p>
            <div className="bg-slate-50 rounded-2xl p-5 text-left space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Reference</span>
                <span className="font-semibold text-slate-800">{paymentData?.registrationCode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Student</span>
                <span className="font-semibold text-slate-800">{paymentData?.formData?.studentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Amount</span>
                <span className="font-black text-sky-600">Rs. {paymentData?.amount}</span>
              </div>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-black text-slate-800 mb-2">Payment Successful</h1>
            <p className="text-slate-600 mb-6">Your registration has been confirmed and the database status was updated.</p>
            <div className="bg-emerald-50 rounded-2xl p-5 mb-6">
              <p className="text-sm text-emerald-700">Registration Reference</p>
              <p className="text-2xl font-black text-emerald-800">{paymentData?.registrationCode}</p>
            </div>
            <Button onClick={() => navigate("/")} className="w-full rounded-full py-6 bg-sky-500 hover:bg-sky-400">
              Go to Home
            </Button>
          </div>
        )}

        {status === "failed" && (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-3xl font-black text-slate-800 mb-2">Payment Not Completed</h1>
            <p className="text-slate-600 mb-4">{error || "The payment could not be completed."}</p>
            <div className="bg-slate-50 rounded-2xl p-5 mb-6">
              <div className="flex justify-between">
                <span className="text-slate-500">Reference</span>
                <span className="font-semibold text-slate-800">{paymentData?.registrationCode}</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => window.location.reload()} className="flex-1 rounded-full py-6 bg-sky-500 hover:bg-sky-400">
                <CreditCard className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate("/register")} className="flex-1 rounded-full py-6">
                Back to Register
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RazorpayPaymentPage;
